import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
const MLBGameCard = ({ game }) => {
    // Check if game is completed and has results
    const isGameFinal = game.result?.final || false;
    const hasUserBet = game.bet_outcome !== undefined;
    const calculateBetGrade = () => {
        // Use bet_ranking if available (check both possible paths)
        if (game.bet_ranking?.grade) {
            return game.bet_ranking.grade;
        }
        if (game.professional_bet_decision?.bet_ranking?.grade) {
            return game.professional_bet_decision.bet_ranking.grade;
        }
        // Otherwise calculate based on confidence and probability
        const confidence = (game.recommended_bet?.probability || 0.5) * 100;
        const confidenceLevel = game.recommended_bet?.confidence_level;
        if (confidenceLevel === 'HIGH' && confidence >= 70)
            return 'A+';
        if (confidenceLevel === 'HIGH' && confidence >= 65)
            return 'A';
        if (confidenceLevel === 'HIGH')
            return 'A-';
        if (confidenceLevel === 'MEDIUM_PLUS' && confidence >= 60)
            return 'B+';
        if (confidenceLevel === 'MEDIUM_PLUS')
            return 'B';
        if (confidenceLevel === 'MEDIUM' && confidence >= 55)
            return 'B-';
        if (confidenceLevel === 'MEDIUM')
            return 'C+';
        if (confidenceLevel === 'LOW')
            return 'C';
        return 'C-';
    };
    const getBetGradeColor = (grade) => {
        if (!grade)
            return '#6c757d';
        const gradeUpper = grade.toUpperCase();
        if (gradeUpper.startsWith('A'))
            return '#198754';
        if (gradeUpper.startsWith('B'))
            return '#0dcaf0';
        if (gradeUpper.startsWith('C'))
            return '#ffc107';
        if (gradeUpper.startsWith('D'))
            return '#dc3545';
        return '#6c757d';
    };
    const formatGameTime = (gameTime) => {
        return new Date(gameTime).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };
    const hasInsufficientValue = () => {
        // Don't show "BAD VALUE" for games with positive odds - those are actually good value
        const odds = getMoneylineOdds();
        const oddsValue = parseInt(odds);
        if (!isNaN(oddsValue) && oddsValue > 0) {
            return false; // Positive odds = good value, don't flag
        }
        // Only flag as bad value if it's a NO_BET with negative odds
        return game.recommended_bet?.bet === 'NO_BET' ||
            game.professional_bet_decision?.should_bet === false ||
            (game.betting_analysis?.best_bet_recommendation?.recommendation === 'NO_BET');
    };
    const getBetDisplayText = () => {
        // Always show the model's pick, even for insufficient value games
        let team;
        let spread = null;
        if (game.recommended_bet?.bet === 'NO_BET') {
            // For NO_BET games, try to get the model's original prediction
            // Check if we have ensemble prediction data
            const ensemblePrediction = game.ensemble_prediction;
            if (ensemblePrediction !== undefined) {
                team = ensemblePrediction === 1 ? (game.game_info?.home_team || 'Home') : (game.game_info?.away_team || 'Away');
            }
            else {
                // Fallback to spread prediction
                if (game.spread_prediction?.home_covers_runline_prob && game.spread_prediction?.away_covers_runline_prob) {
                    team = game.spread_prediction.home_covers_runline_prob > game.spread_prediction.away_covers_runline_prob
                        ? (game.game_info?.home_team || 'Home')
                        : (game.game_info?.away_team || 'Away');
                }
                else {
                    // Final fallback - show home team
                    team = game.game_info?.home_team || 'Home';
                }
            }
            // Use the spread for our predicted winner from professional_bet_decision
            if (ensemblePrediction !== undefined && game.professional_bet_decision) {
                // Use ensemble prediction: 0 = away, 1 = home
                spread = ensemblePrediction === 1
                    ? game.professional_bet_decision.spread_home
                    : game.professional_bet_decision.spread_away;
            }
            else {
                // Fallback to spread_home if no runline_value
                spread = game.spread_prediction?.runline_value || game.professional_bet_decision?.spread_home || null;
            }
        }
        else {
            // Use recommended bet data with professional_bet_decision spreads
            const isHomeTeam = game.recommended_bet?.team === 'HOME';
            team = isHomeTeam
                ? (game.game_info?.home_team || 'Home')
                : (game.game_info?.away_team || 'Away');
            // Always use professional_bet_decision spreads
            spread = isHomeTeam
                ? game.professional_bet_decision?.spread_home || null
                : game.professional_bet_decision?.spread_away || null;
        }
        if (spread === null || spread === undefined) {
            return team; // Just team name if no spread
        }
        const spreadText = spread > 0 ? `+${spread}` : `${spread}`;
        return `${team} ${spreadText}`;
    };
    const getMoneylineOdds = () => {
        // Always show odds for our predicted winner
        let odds = game.recommended_bet?.odds;
        if (!odds && game.professional_bet_decision) {
            // Determine which team we predicted to win and use their spread odds
            const ensemblePrediction = game.ensemble_prediction;
            if (ensemblePrediction !== undefined) {
                // Use ensemble prediction to determine our pick: 0 = away, 1 = home
                odds = ensemblePrediction === 1
                    ? game.professional_bet_decision.spread_home_odds
                    : game.professional_bet_decision.spread_away_odds;
            }
            else if (game.recommended_bet?.team === 'HOME') {
                odds = game.professional_bet_decision.spread_home_odds;
            }
            else if (game.recommended_bet?.team === 'AWAY') {
                odds = game.professional_bet_decision.spread_away_odds;
            }
            else {
                // Fallback: use spread prediction to determine our pick
                if (game.spread_prediction?.home_covers_runline_prob && game.spread_prediction?.away_covers_runline_prob) {
                    odds = game.spread_prediction.home_covers_runline_prob > game.spread_prediction.away_covers_runline_prob
                        ? game.professional_bet_decision.spread_home_odds
                        : game.professional_bet_decision.spread_away_odds;
                }
                else {
                    odds = game.professional_bet_decision.spread_home_odds;
                }
            }
        }
        if (!odds)
            odds = -110; // Final fallback
        return odds > 0 ? `+${odds}` : `${odds}`;
    };
    const getConfidenceColor = () => {
        // Always use recommended_bet confidence_level for color coding
        switch (game.recommended_bet?.confidence_level) {
            case 'HIGH': return 'var(--bs-success)'; // Green for high confidence
            case 'MEDIUM_PLUS': return 'var(--bs-primary)'; // Blue for medium-plus
            case 'MEDIUM': return 'var(--bs-warning)'; // Yellow for medium
            case 'LOW': return 'var(--bs-secondary)'; // Gray for low
            default: return 'var(--bs-secondary)'; // Default gray
        }
    };
    const getPickProbability = () => {
        // Always show the true model confidence, even for NO_BET games
        if (game.recommended_bet?.bet === 'NO_BET') {
            // Try to get the actual model confidence
            const predictionConfidence = game.prediction_confidence;
            if (predictionConfidence) {
                return Math.round(predictionConfidence * 100);
            }
            // Fallback to ensemble probability
            const ensembleProbability = game.ensemble_probability;
            if (ensembleProbability) {
                return Math.round(Math.max(ensembleProbability, 1 - ensembleProbability) * 100);
            }
            // Fallback to spread prediction
            if (game.spread_prediction?.home_covers_runline_prob && game.spread_prediction?.away_covers_runline_prob) {
                return Math.round(Math.max(game.spread_prediction.home_covers_runline_prob, game.spread_prediction.away_covers_runline_prob) * 100);
            }
            return 50; // Neutral fallback
        }
        // Use the probability from recommended_bet which represents confidence in our pick
        return Math.round((game.recommended_bet?.probability || 0.5) * 100);
    };
    const getPickDescription = () => {
        // Always show the model's pick, using the same logic as getBetDisplayText
        let team;
        let spread = null;
        if (game.recommended_bet?.bet === 'NO_BET') {
            // For NO_BET games, try to get the model's original prediction
            const ensemblePrediction = game.ensemble_prediction;
            if (ensemblePrediction !== undefined) {
                team = ensemblePrediction === 1 ? (game.game_info?.home_team || 'Home') : (game.game_info?.away_team || 'Away');
            }
            else {
                // Fallback to spread prediction
                if (game.spread_prediction?.home_covers_runline_prob && game.spread_prediction?.away_covers_runline_prob) {
                    team = game.spread_prediction.home_covers_runline_prob > game.spread_prediction.away_covers_runline_prob
                        ? (game.game_info?.home_team || 'Home')
                        : (game.game_info?.away_team || 'Away');
                }
                else {
                    team = game.game_info?.home_team || 'Home';
                }
            }
            // Use the spread for our predicted winner from professional_bet_decision
            if (ensemblePrediction !== undefined && game.professional_bet_decision) {
                // Use ensemble prediction: 0 = away, 1 = home
                spread = ensemblePrediction === 1
                    ? game.professional_bet_decision.spread_home
                    : game.professional_bet_decision.spread_away;
            }
            else {
                // Fallback to spread_home if no runline_value
                spread = game.spread_prediction?.runline_value || game.professional_bet_decision?.spread_home || null;
            }
        }
        else {
            // Use recommended bet data with professional_bet_decision spreads
            const isHomeTeam = game.recommended_bet?.team === 'HOME';
            team = isHomeTeam
                ? (game.game_info?.home_team || 'Home')
                : (game.game_info?.away_team || 'Away');
            // Always use professional_bet_decision spreads
            spread = isHomeTeam
                ? game.professional_bet_decision?.spread_home || null
                : game.professional_bet_decision?.spread_away || null;
        }
        if (spread !== null && spread !== undefined) {
            const spreadText = spread > 0 ? `+${spread}` : `${spread}`;
            return `${team} ${spreadText}`;
        }
        return team;
    };
    const isProfessionalPick = () => {
        return game.professional_bet_decision?.should_bet === true;
    };
    const meetsProfessionalCriteria = () => {
        // Check if the game has meets_professional_criteria field
        return game.meets_professional_criteria === true;
    };
    const getBadgeText = () => {
        // Use display_classification primary badge if available
        if (game.display_classification?.display_helpers.primary_badge) {
            return game.display_classification.display_helpers.primary_badge;
        }
        // Fallback to legacy logic
        if (game.recommended_bet.bet === 'NO_BET') {
            return 'AVOID';
        }
        return game.recommended_bet.confidence_level;
    };
    // Bet outcome indicator
    const getBetOutcomeIndicator = () => {
        if (!hasUserBet || !isGameFinal)
            return null;
        const betWon = game.bet_outcome?.status === 'won';
        const icon = betWon ? '✓' : '✗';
        const color = betWon ? '#22c55e' : '#dc3545';
        const bgColor = betWon ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)';
        return (_jsx("div", { style: {
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: bgColor,
                border: `2px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                color: color,
                zIndex: 10
            }, title: betWon ? 'Bet Won!' : 'Bet Lost', children: icon }));
    };
    return (_jsx("div", { className: "card h-100", style: {
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }, onMouseEnter: (e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }, onMouseLeave: (e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        }, children: _jsxs("div", { className: "card-body", style: { padding: '16px' }, children: [getBetOutcomeIndicator(), !isGameFinal && (_jsx("div", { className: "d-flex justify-content-between align-items-center mb-3", children: _jsxs("div", { className: "team-info", children: [_jsxs("h5", { className: "mb-0", style: { fontSize: '18px', fontWeight: '600', color: 'var(--bs-body-color)' }, children: [game.game_info?.away_team || 'Away', " @ ", game.game_info?.home_team || 'Home'] }), _jsxs("div", { className: "text-muted", style: { fontSize: '14px' }, children: [game.game_info?.game_time ? formatGameTime(game.game_info.game_time) : 'TBD', " \u2022 ", game.game_info?.venue || 'TBD'] })] }) })), isGameFinal && game.result && (() => {
                    // Determine if prediction was correct
                    const result = game.result;
                    const winner = result.winner;
                    // Determine which team we predicted
                    let predictedTeam;
                    if (game.recommended_bet?.bet !== 'NO_BET') {
                        predictedTeam = game.recommended_bet?.team === 'HOME'
                            ? (game.game_info?.home_team || 'Home')
                            : (game.game_info?.away_team || 'Away');
                    }
                    else {
                        // For NO_BET games, use ensemble prediction
                        const ensemblePrediction = game.ensemble_prediction;
                        predictedTeam = ensemblePrediction === 1
                            ? (game.game_info?.home_team || 'Home')
                            : (game.game_info?.away_team || 'Away');
                    }
                    const homeTeam = game.game_info?.home_team || 'Home';
                    const predictedWinner = predictedTeam === homeTeam ? 'home' : 'away';
                    const wasCorrect = predictedWinner === winner;
                    const borderColor = wasCorrect ? '#22c55e' : '#dc3545';
                    const bgColor = wasCorrect ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)';
                    const labelColor = wasCorrect ? '#22c55e' : '#dc3545';
                    const label = wasCorrect ? 'FINAL - CORRECT' : 'FINAL - INCORRECT';
                    return (_jsxs("div", { className: "mb-3", style: {
                            backgroundColor: bgColor,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '6px',
                            padding: '12px',
                            textAlign: 'center'
                        }, children: [_jsx("div", { style: { fontSize: '11px', color: labelColor, fontWeight: '600', marginBottom: '4px' }, children: label }), _jsxs("div", { style: { fontSize: '20px', fontWeight: '700', color: 'var(--bs-body-color)' }, children: [result.away_score, " - ", result.home_score] }), _jsxs("div", { style: { fontSize: '12px', color: '#6b7280', marginTop: '4px' }, children: ["Winner: ", result.winner === 'home' ? (game.game_info?.home_team || 'Home') : (game.game_info?.away_team || 'Away')] })] }));
                })(), _jsx("div", { className: "betting-line mb-3 bg-light", style: {
                        borderRadius: '6px',
                        padding: '12px'
                    }, children: _jsxs("div", { className: "d-flex justify-content-between align-items-start", children: [_jsxs("div", { children: [_jsx("div", { className: "fw-semibold", style: { fontSize: '16px', color: 'var(--bs-body-color)' }, children: getBetDisplayText() }), game.recommended_bet?.bet === 'NO_BET' && (_jsx("div", { className: "text-secondary", style: { fontSize: '12px' }, children: game.recommended_bet?.filter_reason || 'Insufficient value' }))] }), _jsxs("div", { className: "text-end", children: [_jsx("div", { className: "fw-bold", style: { fontSize: '16px', color: getConfidenceColor() }, children: getMoneylineOdds() }), hasInsufficientValue() && (_jsxs("div", { className: "text-warning", style: { fontSize: '12px', marginTop: '2px' }, children: [_jsx("i", { className: "fas fa-exclamation-triangle me-1" }), "BAD VALUE"] }))] })] }) }), _jsxs("div", { className: "confidence-section mb-3", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "text-muted", style: { fontSize: '12px' }, children: "Pick Confidence" }), _jsxs("span", { style: { fontSize: '12px', fontWeight: '500', color: 'var(--bs-body-color)' }, children: [getPickProbability(), "% ", getPickDescription()] })] }), _jsx("div", { className: "progress", style: { height: '10px', borderRadius: '5px' }, children: _jsx("div", { className: "progress-bar", style: {
                                    width: `${getPickProbability()}%`,
                                    backgroundColor: getConfidenceColor(),
                                    borderRadius: '5px'
                                } }) })] }), _jsx("div", { className: "criteria-status mb-3", children: _jsxs("div", { className: "row g-2", children: [_jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center p-2 bg-light", style: { borderRadius: '4px' }, children: [_jsx("div", { className: "text-secondary", style: { fontSize: '12px' }, children: "Confidence" }), _jsx("div", { style: {
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: getConfidenceColor()
                                            }, children: game.recommended_bet?.confidence_level || 'MEDIUM' })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center p-2 bg-light", style: { borderRadius: '4px' }, children: [_jsx("div", { className: "text-secondary", style: { fontSize: '12px' }, children: "Bet Grade" }), _jsx("div", { style: {
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: getBetGradeColor(calculateBetGrade())
                                            }, children: calculateBetGrade() })] }) })] }) }), _jsx("div", { className: "pitcher-matchup mb-3", children: _jsxs("div", { className: "row g-2 text-center", children: [_jsxs("div", { className: "col-6", children: [_jsx("div", { className: "text-uppercase text-muted", style: { fontSize: '10px' }, children: "Away SP" }), _jsx("div", { className: "text-truncate", style: { fontSize: '12px', fontWeight: '500', color: 'var(--bs-body-color)' }, children: game.game_info?.away_pitcher_name || 'TBD' }), _jsx("div", { className: "text-muted", style: { fontSize: '10px' }, children: game.features_used?.away_pitcher_fip ?
                                            `${game.features_used.away_pitcher_fip} FIP` :
                                            'Pitcher Stats' })] }), _jsxs("div", { className: "col-6", children: [_jsx("div", { className: "text-uppercase text-muted", style: { fontSize: '10px' }, children: "Home SP" }), _jsx("div", { className: "text-truncate", style: { fontSize: '12px', fontWeight: '500', color: 'var(--bs-body-color)' }, children: game.game_info?.home_pitcher_name || 'TBD' }), _jsx("div", { className: "text-muted", style: { fontSize: '10px' }, children: game.features_used?.home_pitcher_fip ?
                                            `${game.features_used.home_pitcher_fip} FIP` :
                                            'Pitcher Stats' })] })] }) }), _jsxs(Link, { to: `/dashboard/game/${game.game_info?.game_id || 'unknown'}`, className: "btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center", style: {
                        fontSize: '12px',
                        fontWeight: '500',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        minHeight: '44px'
                    }, children: [_jsx("i", { className: "fas fa-chart-line me-2" }), "View Analysis"] })] }) }));
};
export default MLBGameCard;
